import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { AppImage } from '../components/AppImage';
import { Container } from '../components/Container';
import { Section } from '../components/Section';
import { Reveal } from '../components/Reveal';
import { PageIntro } from '../components/PageIntro';
import { CaseStudyHero } from '../components/case-study/CaseStudyHero';
import { OverviewSidebar } from '../components/case-study/OverviewSidebar';
import { Chapter } from '../components/case-study/Chapter';
import { ArtifactFigure } from '../components/case-study/ArtifactFigure';
import { DecisionList } from '../components/case-study/DecisionList';
import { DesignDirectionBlock } from '../components/case-study/DesignDirectionBlock';
import { MockupShowcase } from '../components/case-study/MockupShowcase';
import { BeforeAfter } from '../components/case-study/BeforeAfter';
import { ResultsBlock } from '../components/case-study/ResultsBlock';
import { ClientQuote } from '../components/case-study/ClientQuote';
import { NextCaseStudy } from '../components/case-study/NextCaseStudy';
import { CtaBand } from '../components/home/CtaBand';
import { projects } from '../data/projects';
import { getCaseStudy } from '../data/caseStudies';
import { getCaseStudyImages } from '../data/caseStudyImages';
export function CaseStudy() {
  const {
    slug
  } = useParams();
  const projectIndex = projects.findIndex(item => item.slug === slug);
  const project = projectIndex >= 0 ? projects[projectIndex] : undefined;
  const study = getCaseStudy(slug);
  if (!project || !study) {
    return <>
        <PageIntro eyebrow="Case study" title="We could not find that project." lede="It may have been renamed. Everything we have published lives on the work page." data-id="element-923" data-mp="327mazlasj62f" />
        <Section bordered data-id="element-924" data-mp="hf0w9eeoilin">
          <Container data-id="element-925" data-mp="hif0ytatf6t9">
            <Link to="/work" className="inline-flex items-center gap-2 text-small text-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-926" data-mp="chr6fxndo7ua">
              <ArrowLeftIcon size={16} aria-hidden="true" data-id="element-927" data-mp="3b8bmhnmqxtn4" />
              Back to all work
            </Link>
          </Container>
        </Section>
      </>;
  }
  const nextProject = projects[(projectIndex + 1) % projects.length];
  const images = getCaseStudyImages(study.slug);
  return <>
      <CaseStudyHero project={project} study={study} heroImage={images.hero} data-id="element-928" data-mp="2nj99it324oyy" />

      <Section labelledBy="problem-title" data-id="element-929" data-mp="2nh68zf4a3dfz">
        <Container data-id="element-930" data-mp="2rlmcxauc0ask">
          <div className="grid gap-14 lg:grid-cols-12 lg:gap-10" data-id="element-931" data-mp="2rl46xmhvai7g">
            <div className="lg:col-span-4" data-id="element-932" data-mp="2wjfhjzfzkwqq">
              <Reveal data-id="element-933" data-mp="2wicslx2854dg">
                <OverviewSidebar project={project} study={study} data-id="element-934" data-mp="31adzf3gmi2w3" />
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:col-start-6" data-id="element-935" data-mp="23v08dtjjd8nb">
              <Chapter chapter={study.problem} id="problem-title" image={images.problem} data-id="element-936" data-mp="23uqdbrjmwbar" />
            </div>
          </div>
        </Container>
      </Section>

      <Section tone="surface" bordered labelledBy="research-title" data-id="element-937" data-mp="21b3pqw50v164">
        <Container data-id="element-938" data-mp="2187s78x36ei7">
          <Chapter chapter={study.research} id="research-title" image={images.research} data-id="element-939" data-mp="1wazb7bnocgij" />
          <div className="mt-16 grid gap-10 md:grid-cols-3 md:gap-8 lg:mt-20" data-id="element-940" data-mp="1rc4h3dk9b5j4">
            {study.artifacts.map((artifact, index) => <Reveal key={artifact.title} delay={index * 0.06} data-id="element-941" data-mp="1na142khxvsvq" data-mpi={index}>
                <ArtifactFigure artifact={artifact} data-id="element-942" data-mp="1ibg66rq5tlag" />
              </Reveal>)}
          </div>
        </Container>
      </Section>

      <Section bordered labelledBy="strategy-title" data-id="element-943" data-mp="1dk7vzth09vw8">
        <Container data-id="element-944" data-mp="18ln2se068cha">
          <Chapter chapter={study.strategy} id="strategy-title" image={images.strategy} data-id="element-945" data-mp="18l51h2ynj82i" />
          <div className="mt-16 lg:mt-20" data-id="element-946" data-mp="13nbsk25yrzyj">
            <p className="mb-6 text-[12px] uppercase tracking-[0.06em] text-muted" data-id="element-947" data-mp="13omyo8mvtjst">
              The decisions we made, and why
            </p>
            <DecisionList decisions={study.decisions} data-id="element-948" data-mp="umdl19wbhab7" />
          </div>
        </Container>
      </Section>

      <Section tone="surface" bordered labelledBy="design-title" data-id="element-949" data-mp="2unv4400lo3a7">
        <Container data-id="element-950" data-mp="2uqr0vl0polx2">
          <Chapter chapter={study.design} id="design-title" image={images.design} data-id="element-951" data-mp="2ypqd7qorvkyj" />
          <div className="mt-16 lg:mt-20" data-id="element-952" data-mp="2kyla2em74ajg">
            <DesignDirectionBlock study={study} data-id="element-953" data-mp="2kximafk66o7r" />
          </div>
        </Container>
      </Section>

      <Section bordered labelledBy="mockups-title" data-id="element-954" data-mp="2pwmup3z9shx1">
        <Container data-id="element-955" data-mp="2b0dvg2fdq4km">
          <Reveal data-id="element-956" data-mp="2azw3hhzr2cii">
            <h2 id="mockups-title" className="max-w-[18ch] text-[32px] leading-[1.1] tracking-[-0.03em] text-ink lg:text-[40px]" data-id="element-957" data-mp="2azcj7ummnc1b">
              The finished design
            </h2>
          </Reveal>
          <div className="mt-14 lg:mt-16" data-id="element-958" data-mp="221sicmsofhr8">
            <MockupShowcase project={project} caption={study.mockupCaption} data-id="element-959" data-mp="26yakrg0jb40e" />
          </div>

          {study.before && <div className="mt-20 border-t border-hairline pt-16 lg:mt-24 lg:pt-20" data-id="element-960" data-mp="9nr7dj04ohk">
              <Reveal data-id="element-961" data-mp="56e7a0ut3k2z">
                <h3 className="max-w-[20ch] text-[24px] leading-[1.2] tracking-[-0.025em] text-ink" data-id="element-962" data-mp="56xgmt6e505e">
                  What it moves away from
                </h3>
              </Reveal>
              <div className="mt-10" data-id="element-963" data-mp="adkn6kn9t74p">
                <BeforeAfter beforeImage={study.before.image} beforeAlt={study.before.alt} afterImage={project.screenshot} afterAlt={project.screenshotAlt} data-id="element-964" data-mp="aageb9yv1n7p" />
              </div>
            </div>}
        </Container>
      </Section>

      <Section tone="surface" bordered labelledBy="results-title" data-id="element-965" data-mp="t3ahl6n4fqw3">
        <Container data-id="element-966" data-mp="t553ooa4pp1r">
          <Reveal data-id="element-967" data-mp="172a52aywdzqo">
            <AppImage src={images.outcome.image} alt={images.outcome.alt} ratio="16:10" className="mb-16 lg:mb-20" data-id="element-968" data-mp="172tro4zhtcb1" />
          </Reveal>
          <ResultsBlock results={study.results} data-id="element-969" data-mp="38t589nvfp17f" />
          {study.quote && <div className="mt-20 border-t border-hairline pt-16 lg:mt-24 lg:pt-20" data-id="element-970" data-mp="34stm26nfastu">
              <ClientQuote quote={study.quote} data-id="element-971" data-mp="17rp7dylxycfp" />
            </div>}
        </Container>
      </Section>

      <NextCaseStudy project={nextProject} data-id="element-972" data-mp="1cq8s5vosti6h" />

      <CtaBand data-id="element-973" data-mp="1cradqu3fxn2g" />
    </>;
}